// Admin navigation has six sections; subpages stay within their section.
// Existing page URLs remain valid, including bookmarks to Users and Dancefloor.
import type { ReactElement } from 'react'
import { createFileRoute, Link, Outlet, redirect, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Activity, Library, MessageSquare, Radio, Settings2, ShieldCheck, type LucideIcon } from 'lucide-react'
import { ViewAsBanner } from '@/components/admin/ViewAsControl'
import { FlowStatePill } from '@/components/flows/FlowStatePill'
import { getFlowState } from '@/components/flows/flow-state'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { apiClient, scopedApiPath } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import { useViewAsStore } from '@/stores/view-as-store'
import type { FlowListItem, FlowsListResponse, StreamSummary, SystemSettings } from '@/lib/types'

export const Route = createFileRoute('/admin')({
  beforeLoad: () => {
    const { token, user } = useAuthStore.getState()
    if (token === null || (user?.role !== 'admin' && user?.role !== 'super_admin')) {
      throw redirect({ to: '/login', replace: true })
    }
  },
  component: AdminLayout,
})

type AdminPage = {
  value: string
  label: string
  to: '/admin' | '/admin/streams' | '/admin/live-mixer' | '/admin/channels'
    | '/admin/recordings' | '/admin/transcripts' | '/admin/chat' | '/admin/dancefloor'
    | '/admin/settings' | '/admin/users' | '/admin/super-admin'
}
type AdminSection = {
  value: string
  label: string
  icon: LucideIcon
  superAdminOnly?: boolean
  pages: readonly AdminPage[]
}
const ADMIN_SECTIONS: readonly AdminSection[] = [
  { value: 'overview', label: 'Overview', icon: Activity, pages: [
    { value: 'overview', label: 'Overview', to: '/admin' },
  ] },
  { value: 'live', label: 'Live', icon: Radio, pages: [
    { value: 'streams', label: 'Stream keys', to: '/admin/streams' },
    { value: 'channels', label: 'Channels', to: '/admin/channels' },
  ] },
  { value: 'library', label: 'Library', icon: Library, pages: [
    { value: 'recordings', label: 'Recordings', to: '/admin/recordings' },
  ] },
  { value: 'chat', label: 'Chat', icon: MessageSquare, pages: [
    { value: 'chat', label: 'Chat & controls', to: '/admin/chat' },
    { value: 'dancefloor', label: 'Dancefloor', to: '/admin/dancefloor' },
  ] },
  { value: 'settings', label: 'Settings', icon: Settings2, pages: [
    { value: 'settings', label: 'Settings', to: '/admin/settings' },
  ] },
  { value: 'super-admin', label: 'Super admin', icon: ShieldCheck, superAdminOnly: true, pages: [
    { value: 'users', label: 'Users & invites', to: '/admin/users' },
    { value: 'super-admin', label: 'Server & accounts', to: '/admin/super-admin' },
  ] },
]

function AdminLayout() {
  const pathname = useRouterState({ select: state => state.location.pathname }).replace(/\/+$/, '')
  const section = ADMIN_SECTIONS.find(item => item.pages.some(page => page.to === pathname) || (item.value === 'library' && pathname === '/admin/transcripts') || (item.value === 'live' && pathname === '/admin/live-mixer')) ?? ADMIN_SECTIONS[0]
  const page: AdminPage = pathname === '/admin/transcripts'
    ? { value: 'transcripts', label: 'Transcripts', to: '/admin/transcripts' }
    : pathname === '/admin/live-mixer'
      ? { value: 'live-mixer', label: 'Live mixer', to: '/admin/live-mixer' }
    : section.pages.find(item => item.to === pathname) ?? section.pages[0]
  useDocumentTitle(page.label)
  const isSuperAdmin = useAuthStore(s => s.user?.role === 'super_admin')
  const viewAsId = useViewAsStore(s => s.viewAsId)
  const globalPage = section.value === 'super-admin'

  return (
    <main data-testid="admin-layout" className="mx-auto max-w-[1536px] px-4 pb-16 sm:px-6">
      {isSuperAdmin && viewAsId != null && <ViewAsBanner viewAsId={viewAsId} globalPage={globalPage} />}
      <div className="admin-masthead relative mt-3 overflow-hidden rounded-t-xl border border-b-0 border-cyber-cyan/20 px-5 py-4 sm:mt-5 sm:px-7 sm:py-6">
        <div className="relative z-10 space-y-1 sm:space-y-2">
          <p className="text-sm font-medium text-cyber-cyan">{globalPage ? 'LiveVane · Server administration' : 'LiveVane · Broadcast studio'}</p>
          <h1 className="font-display text-3xl leading-none tracking-wide sm:text-5xl">{page.label}</h1>
          {globalPage ? <p className="text-sm text-muted-foreground">People, access and shared services. One place to run your server.</p>
            : <OnAirStatusLine viewAsId={viewAsId} />}
        </div>
      </div>
      <AdminNavigation section={section} page={page} isSuperAdmin={isSuperAdmin} />
      <div className="pt-6"><Outlet /></div>
    </main>
  )
}

function AdminNavigation({ section, page, isSuperAdmin }: { section: AdminSection; page: AdminPage; isSuperAdmin: boolean }) {
  return (
    <div className="admin-navigation sticky top-14 z-20 rounded-b-xl border border-cyber-cyan/20">
      <nav aria-label="Admin sections" data-testid="admin-tabs"
        className="relative grid grid-cols-3 gap-0 px-2 sm:flex sm:gap-1 sm:px-3">
        {ADMIN_SECTIONS.filter(item => !item.superAdminOnly || isSuperAdmin).map(item => (
          <Link key={item.value} to={item.pages[0].to} activeOptions={{ exact: true }} data-testid={`admin-tab-${item.value}`}
            aria-current={section.value === item.value ? 'page' : undefined}
            className={cn('admin-section-link flex min-h-12 min-w-0 shrink-0 items-center justify-center gap-2 whitespace-nowrap border-b-2 border-transparent px-2 text-xs sm:text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:px-4',
              section.value === item.value && 'is-active border-cyber-cyan text-cyber-cyan', item.superAdminOnly && 'sm:ml-auto')}>
            <item.icon aria-hidden="true" className="hidden size-4 sm:block" />{item.label}
          </Link>
        ))}
      </nav>
      {section.pages.length > 1 && <nav aria-label={`${section.label} pages`} data-testid="admin-subnav"
        className="flex flex-wrap items-center gap-1 border-t border-border/60 px-3 py-2 sm:px-5">
        {section.pages.map(item => <Link key={item.value} to={item.to} data-testid={`admin-subnav-${item.value}`}
          aria-current={page.value === item.value ? 'page' : undefined}
          className={cn('inline-flex min-h-10 items-center rounded-md px-3 text-sm transition-colors hover:bg-white/5 hover:text-foreground',
            page.value === item.value ? 'bg-cyber-cyan/10 font-medium text-cyber-cyan' : 'text-muted-foreground')}>
          {item.label}
        </Link>)}
      </nav>}
    </div>
  )
}

type StreamsListResponse = StreamSummary[] | { streams?: StreamSummary[] }

function normalizeStreams(payload: StreamsListResponse): StreamSummary[] {
  if (Array.isArray(payload)) return payload
  return payload.streams ?? []
}

function formatUptime(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  if (totalMinutes > 0) return `${minutes}m`
  return 'just now'
}

function OnAirStatusLine({ viewAsId }: { viewAsId: number | null }): ReactElement {
  const vaSuffix: string = viewAsId == null ? '' : `view_as=${String(viewAsId)}`

  const streamsQuery = useQuery<StreamsListResponse, Error, StreamSummary[]>({
    queryKey: ['admin', 'overview', 'streams'],
    queryFn: () => apiClient.get<StreamsListResponse>('/streams/'),
    select: normalizeStreams,
    refetchInterval: 5000,
  })
  const flowsQuery = useQuery({
    queryKey: vaSuffix === '' ? ['flows'] : ['flows', vaSuffix],
    queryFn: () =>
      apiClient.get<FlowsListResponse>(scopedApiPath('/flows/', viewAsId)),
    refetchInterval: 5000,
  })
  const systemQuery = useQuery<SystemSettings>({
    queryKey: ['admin', 'system-settings', vaSuffix],
    queryFn: () =>
      apiClient.get<SystemSettings>(scopedApiPath('/system/settings/', viewAsId)),
  })

  const loading =
    streamsQuery.isPending || flowsQuery.isPending || systemQuery.isPending
  const failed = streamsQuery.isError || flowsQuery.isError || systemQuery.isError

  if (loading) {
    return (
      <p className="min-h-5 text-sm text-muted-foreground" aria-live="polite">
        Checking what is on air…
      </p>
    )
  }
  if (failed) {
    return (
      <p className="min-h-5 text-sm text-muted-foreground" role="status">
        Live status unavailable right now.
      </p>
    )
  }

  const liveKeys = (streamsQuery.data ?? []).filter((s) => s.is_live).length
  const flows: FlowListItem[] = flowsQuery.data?.flows ?? []
  const onAir = flows.filter((f) => getFlowState(f) === 'on-air')
  const broadcasting = systemQuery.data?.system?.broadcasting === true

  const separator = (
    <span aria-hidden="true" className="px-1.5 text-muted-foreground/60">
      ·
    </span>
  )

  return (
    <p
      className="flex min-h-5 flex-wrap items-center text-sm text-muted-foreground"
      role="status"
      aria-live="polite"
      data-testid="admin-on-air-status"
    >
      <span className="tabular-nums">
        {liveKeys === 0
          ? 'No keys live'
          : `${liveKeys} ${liveKeys === 1 ? 'key' : 'keys'} live`}
      </span>
      {onAir.map((flow) => (
        <span key={flow.ID} className="inline-flex items-center">
          {separator}
          <FlowStatePill state="on-air" size="sm" className="mr-1.5" />
          <span className="text-foreground">{flow.NAME}</span>
          {flow.LIVE ? (
            <span className="ml-1.5 tabular-nums">{formatUptime(flow.LIVE.uptime_ms)}</span>
          ) : null}
        </span>
      ))}
      <span className="inline-flex items-center">
        {separator}
        Broadcasting {broadcasting ? 'on' : 'off'}
      </span>
    </p>
  )
}
